import React from 'react';
import { Trophy, Sigma, Sparkles, BadgeCheck } from 'lucide-react';
import { BrandIcon } from '../components/BrandIcon';

const Awards = () => {
  const credentials = [
    {
      icon: Trophy,
      title: '5x Stevie Award Winner',
      organization: 'Stevie Awards',
      year: '2019–2024',
      description: 'Multiple awards for customer service excellence and innovation'
    },
    {
      icon: Sigma,
      title: 'Lean Six Sigma Certified',
      description: 'Process improvement methodology for operational excellence'
    },
    {
      icon: Sparkles,
      title: 'Google AI Professional Certificate',
      organization: 'Google · Coursera',
      year: '2026',
      description: 'Formal training in applied AI — directly informing the AI-powered support infrastructure I build for clients'
    },
    {
      icon: BadgeCheck,
      title: 'ITIL 4 Foundation & Managing Professional (MP)',
      organization: 'PeopleCert / AXELOS',
      year: '2026',
      description: 'The global best-practice framework for IT and support service management — aligning support operations with proven, scalable processes'
    }
  ];

  return (
    <div className="min-h-screen py-20 md:py-32 bg-white" data-testid="awards-page">
      <div className="container mx-auto px-[7.6923%]">
        {/* Header */}
        <div className="text-center mb-20">
          <h1 className="text-[36px] sm:text-[32px] md:text-[48px] lg:text-[66px] font-semibold leading-[1.1] tracking-[-0.62px] text-gray-900 mb-6">
            Awards & Credentials
          </h1>
          <p className="text-[20px] text-gray-900/85 max-w-3xl mx-auto">
            Recognized excellence in customer experience, backed by 15+ years of building and scaling CX teams.
          </p>
        </div>

        {/* Credentials Grid */}
        <div className="grid md:grid-cols-2 gap-10 max-w-4xl mx-auto mb-8">
          {credentials.map((credential, index) => (
            <div
              key={index}
              data-testid={`credential-card-${index}`}
              className="bg-gray-50 p-6 sm:p-12 border border-gray-200 hover:border-[#00D4B4] hover:shadow-lg transition-all duration-400 group"
            >
              <BrandIcon icon={credential.icon} className="mb-8 group-hover:scale-110 transition-transform duration-400" />
              <h3 className="text-[28px] font-semibold text-gray-900 mb-3">
                {credential.title}
              </h3>
              {(credential.organization || credential.year) && (
                <div className="flex items-center gap-3 mb-4">
                  {credential.organization && (
                    <span className="text-[16px] text-gray-900/70">{credential.organization}</span>
                  )}
                  {credential.organization && credential.year && (
                    <span className="text-gray-900/40">•</span>
                  )}
                  {credential.year && (
                    <span className="text-[16px] text-[#00D4B4]">{credential.year}</span>
                  )}
                </div>
              )}
              <p className="text-[18px] text-gray-900/85 leading-[1.6]">
                {credential.description}
              </p>
            </div>
          ))}
        </div>

        <p className="text-center text-[16px] text-gray-900/60 mb-32">
          Currently pursuing additional CX certifications.
        </p>

        {/* Why Work With CX Sage */}
        <div className="max-w-4xl mx-auto text-center" data-testid="why-work-section">
          <h2 className="text-[32px] md:text-[48px] font-semibold leading-[1.1] tracking-[-0.02em] text-gray-900 mb-8">
            Why Work With CX Sage
          </h2>
          {/* Swap in real client testimonials here once you have them. */}
          <p className="text-[20px] text-gray-900/85 leading-[1.6]">
            I've built and scaled CX teams inside PE-backed SaaS and marketplace companies for over 15 years — not just consulted on paper. When we work together, you're getting someone who's done the job, not just studied it.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Awards;
